import {
  Create,
  Edit,
  SimpleForm,
  TextInput,
  NumberInput,
  TextField,
  NumberField,
  DeleteButton,
  BulkDeleteButton,
  CreateButton,
  ExportButton,
  SelectColumnsButton,
  TopToolbar,
  ListContextProvider,
  ResourceContextProvider,
  useListController,
  required,
  useEditContext,
} from "react-admin";
import { useParams } from "react-router-dom";
import { Box } from "@mui/material";
import { Category as CategoryIcon } from "@mui/icons-material";
import { ResourceName } from "@/types";
import List from "@/components/ui/react-admin/styledList/StyledList";
import { StyledDatagridConfigurable } from "@/components/ui/react-admin/StyledDatagrid";
import { PageHeader } from "@/components/common/PageHeader";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";

// KB category management (Feature 4317 / N7, #5430). Rendered as CustomRoutes —
// categories have no standalone permission resource (writes are gated by the
// `help-articles` permission server-side), so this is kept off the permission-
// filtered Resource list and reached from the article list's "Manage categories"
// action. Slug is immutable (stable deep links); the API derives it on create.

const CATEGORY_LIST_PATH = "/help-categories";

const HelpCategoryListActions = ({ loading }: { loading?: boolean }) => {
  const { canCreate } = useResourcePermissions(ResourceName.HELP_ARTICLES);
  return (
    <TopToolbar>
      {canCreate && <CreateButton resource={ResourceName.HELP_CATEGORIES} />}
      <SelectColumnsButton disabled={loading} />
      <ExportButton maxResults={50000} disabled={loading} />
    </TopToolbar>
  );
};

/**
 * Category management grid (N7, #5430) — same UX components as the article list
 * (PageHeader + toolbar, StyledDatagridConfigurable, row + bulk delete). Rendered
 * as a CustomRoute, so the resource is provided explicitly via context
 * (List/Datagrid/DeleteButton and rowClick="edit" resolve the resource and the
 * /help-categories/{id} edit path from it) and a ListController feeds the header
 * actions. Writes are SuperAdmin-only, so create/delete are permission-gated.
 */
export const HelpCategoryList = () => {
  const { canDelete } = useResourcePermissions(ResourceName.HELP_ARTICLES);

  const listContext = useListController({
    resource: ResourceName.HELP_CATEGORIES,
    sort: { field: "sort_order", order: "ASC" },
    perPage: 100,
  });
  const { isPending: loading } = listContext;

  return (
    <ResourceContextProvider value={ResourceName.HELP_CATEGORIES}>
      <ListContextProvider value={listContext}>
        <PageHeader
          customTitle="Help Categories"
          customSubtitle="Create, rename, reorder, or remove Knowledge Base categories."
          hideSectionTitle
          rightContent={<HelpCategoryListActions loading={loading} />}
          headerIcon={<CategoryIcon sx={{ fontSize: 28, color: "primary.main" }} />}
        />
        <List resource={ResourceName.HELP_CATEGORIES} actions={false} loading={loading}>
          <StyledDatagridConfigurable
            rowClick="edit"
            bulkActionButtons={canDelete ? <BulkDeleteButton mutationMode="pessimistic" /> : false}
          >
            <NumberField source="sort_order" label="Order" textAlign="left" />
            <TextField source="name" />
            <TextField source="slug" />
            <TextField source="description" emptyText="—" />
            {canDelete && <DeleteButton mutationMode="pessimistic" redirect={CATEGORY_LIST_PATH} />}
          </StyledDatagridConfigurable>
        </List>
      </ListContextProvider>
    </ResourceContextProvider>
  );
};

const categoryFormFields = (
  <>
    <TextInput source="name" validate={required()} fullWidth />
    <TextInput source="description" fullWidth multiline helperText="Shown under the category card in the Help Center" />
    <NumberInput source="sort_order" label="Order" min={1} helperText="Lower shows first; blank = end" />
  </>
);

export const HelpCategoryCreate = () => (
  <>
    <PageHeader
      customTitle="New Category"
      customSubtitle="The slug is derived from the name automatically."
      hideSectionTitle
      headerIcon={<CategoryIcon sx={{ fontSize: 28, color: "primary.main" }} />}
    />
    <Create resource={ResourceName.HELP_CATEGORIES} redirect={CATEGORY_LIST_PATH} title=" " component={Box}>
      <SimpleForm>{categoryFormFields}</SimpleForm>
    </Create>
  </>
);

const EditHeader = () => {
  const { record } = useEditContext<{ name?: string }>();
  return (
    <PageHeader
      customTitle={record?.name || "Edit Category"}
      customSubtitle="Rename, edit the description, or change the order. The slug stays fixed."
      hideSectionTitle
      headerIcon={<CategoryIcon sx={{ fontSize: 28, color: "primary.main" }} />}
    />
  );
};

export const HelpCategoryEdit = () => {
  const { id } = useParams();
  return (
    <Edit
      resource={ResourceName.HELP_CATEGORIES}
      id={id}
      redirect={CATEGORY_LIST_PATH}
      mutationMode="pessimistic"
      title=" "
      component={Box}
    >
      <EditHeader />
      <SimpleForm>
        <TextInput source="slug" disabled fullWidth helperText="Immutable — used by contextual-help deep links" />
        {categoryFormFields}
      </SimpleForm>
    </Edit>
  );
};
